/**
 * Foto im Browser und am Desktop — `getUserMedia`, ein Standbild, eigene Bühne.
 *
 * `@capacitor/camera` braucht ausserhalb des Geräts die Fremdkomponente `pwa-camera-modal`;
 * ohne sie tat der Knopf nichts (2026-11-01). Entscheidung des Nutzers: eigene Bühne nach
 * dem Muster von `videoWeb.ts`, kein Fremdpaket.
 */

export interface FotoBuehne {
  /** Zeigt die Vorschau und wartet auf „Auslösen" oder „Abbrechen". */
  oeffne(strom: MediaStream): Promise<'ausloesen' | 'abbruch'>;
  /** Das aktuelle Vorschaubild als `data:image/jpeg;base64,…`. */
  bild(): string;
  schliesse(): void;
}

export interface FotoWebDeps {
  quelle: () => Promise<MediaStream>;
  buehne: () => FotoBuehne;
}

const VORGABE: FotoWebDeps = {
  quelle: () => navigator.mediaDevices.getUserMedia({ video: true, audio: false }),
  buehne: domFotoBuehne,
};

export async function fotoImBrowser(deps: FotoWebDeps = VORGABE): Promise<{ base64: string; format: 'jpeg' } | null> {
  const strom = await deps.quelle();
  const buehne = deps.buehne();
  try {
    if ((await buehne.oeffne(strom)) === 'abbruch') return null;
    return { base64: buehne.bild().replace(/^data:[^,]*,/, ''), format: 'jpeg' };
  } finally {
    buehne.schliesse();
    for (const spur of strom.getTracks()) spur.stop();
  }
}

const KNOPF =
  'appearance:none;border:0;border-radius:10px;padding:10px 16px;font:600 15px system-ui;' +
  'background:#ffffff1a;color:#fff;cursor:pointer';

/** Reines DOM wie die Videobühne; Beschriftung deutsch wie dort. */
export function domFotoBuehne(): FotoBuehne {
  let huelle: HTMLDivElement | null = null;
  let vorschau: HTMLVideoElement | null = null;

  return {
    oeffne(strom) {
      return new Promise((fertig) => {
        huelle = document.createElement('div');
        // `pointer-events:auto`: ein offener Radix-Dialog setzt `none` auf `body`.
        huelle.style.cssText =
          'position:fixed;inset:0;z-index:2000;background:#000;display:flex;pointer-events:auto;' +
          'flex-direction:column;align-items:center;justify-content:center;gap:16px';
        huelle.setAttribute('data-buehne', ''); // s. `ui/buehne.ts`
        vorschau = document.createElement('video');
        vorschau.autoplay = true;
        vorschau.muted = true;
        vorschau.playsInline = true;
        vorschau.style.cssText = 'max-width:100%;max-height:70vh;background:#111';
        vorschau.srcObject = strom;

        const leiste = document.createElement('div');
        leiste.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center';
        const knopf = (text: string, wahl: 'ausloesen' | 'abbruch') => {
          const b = document.createElement('button');
          b.type = 'button';
          b.style.cssText = KNOPF;
          b.textContent = text;
          b.onclick = () => fertig(wahl);
          return b;
        };
        leiste.append(knopf('Auslösen', 'ausloesen'), knopf('Abbrechen', 'abbruch'));
        huelle.append(vorschau, leiste);
        document.body.append(huelle);
      });
    },

    bild() {
      const v = vorschau!;
      const leinwand = document.createElement('canvas');
      leinwand.width = v.videoWidth;
      leinwand.height = v.videoHeight;
      leinwand.getContext('2d')!.drawImage(v, 0, 0);
      // 0,82 wie `quality: 82` am Gerät (`camera.ts`).
      return leinwand.toDataURL('image/jpeg', 0.82);
    },

    schliesse() {
      huelle?.remove();
      huelle = null;
      vorschau = null;
    },
  };
}
